import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Play,
  CheckCircle,
  Circle,
  Link2,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { LessonBlock, useLessonBuilderStore } from '../../stores/lessonBuilderStore';

const RIVE_BASE =
  import.meta.env.PROD
    ? 'https://api.clarityofconcept.com'
    : (import.meta.env.VITE_LESSON_API_URL?.replace(/\/api$/, '') || 'http://localhost:8000');

const resolveRiveUrl = (url?: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${RIVE_BASE}${url}`;
};

const sanitizeHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 'mark', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'a', 'br', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^https?:/i,
  });

const RivePreview: React.FC<{
  assetUrl?: string;
  artboard?: string;
  stateMachine?: string;
  autoplay?: boolean;
  loop?: boolean | string;
}> = ({ assetUrl, artboard, stateMachine, autoplay = true, loop = 'auto' }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ artboards: string[]; stateMachines: string[]; animations: string[] }>({
    artboards: [],
    stateMachines: [],
    animations: [],
  });
  const [inputs, setInputs] = useState<string[]>([]);
  const resolvedUrl = useMemo(() => resolveRiveUrl(assetUrl), [assetUrl]);

  useEffect(() => {
    let rive: any;
    let cancelled = false;
    setError(null);
    setMeta({ artboards: [], stateMachines: [], animations: [] });
    setInputs([]);

    if (!resolvedUrl || !canvasRef.current) return;

    const load = async () => {
      try {
        const { Rive, Layout, Fit, Alignment } = await import('@rive-app/canvas');
        if (cancelled || !canvasRef.current) return;

        // Match canvas pixel dimensions to the displayed size for crisp rendering
        const setCanvasSize = () => {
          if (!canvasRef.current || !containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          canvasRef.current.width = rect.width * dpr;
          canvasRef.current.height = rect.height * dpr;
        };
        setCanvasSize();

        rive = new Rive({
          src: resolvedUrl,
          canvas: canvasRef.current,
          artboard: artboard || undefined,
          stateMachines: stateMachine ? [stateMachine] : undefined,
          autoplay: autoplay !== false,
          // @ts-ignore
          locateFile: () => '/rive.wasm?v=local',
          layout: new Layout({
            fit: Fit.Contain,
            alignment: Alignment.Center,
          }),
        });
        rive?.enableMouseEvents?.(true);
        rive?.on?.('loaderror', (msg: any) => {
          const friendly = typeof msg === 'string' ? msg : msg?.message || 'Load error';
          setError(friendly);
        });
        const handleLoaded = () => {
          try {
            const smNames = rive?.stateMachineNames?.() || [];
            const animNames = rive?.animationNames?.() || [];
            const artNames = rive?.artboardNames?.() || [];
            setMeta({
              artboards: artNames,
              stateMachines: smNames,
              animations: animNames,
            });
            const inputNames =
              (smNames && smNames.length && rive?.stateMachineInputs ? rive.stateMachineInputs(smNames[0])?.map((i: any) => i.name) : []) ||
              [];
            setInputs(inputNames);

            // Validate names
            if (artboard && artNames.length && !artNames.includes(artboard)) {
              setError(`Artboard "${artboard}" not found. Available: ${artNames.join(', ') || 'none'}`);
            }
            if (stateMachine && smNames.length && !smNames.includes(stateMachine)) {
              setError(`State machine "${stateMachine}" not found. Available: ${smNames.join(', ') || 'none'}`);
            }

            // If no explicit stateMachine provided, auto-pick first state machine or first animation
            if (!stateMachine) {
              if (smNames.length > 0) {
                rive?.play(smNames[0]);
              } else if (animNames.length > 0) {
                rive?.play(animNames[0]);
              }
            }

            if (loop === false) {
              rive?.on('loopEnd', () => rive?.stop());
            }
          } catch {
            /* noop */
          }
        };

        rive?.once?.('load', handleLoaded);
        // If already loaded (cache), run handler
        handleLoaded();

        // Resize observer to keep canvas square and crisp
        const observer = new ResizeObserver(() => setCanvasSize());
        if (containerRef.current) {
          observer.observe(containerRef.current);
        }
        return () => {
          observer.disconnect();
        };
      } catch (e: any) {
        console.error('Rive preview failed', e);
        setError('Preview unavailable');
      }
    };
    load();

    return () => {
      cancelled = true;
      try {
        rive?.cleanup?.();
      } catch {
        /* noop */
      }
    };
  }, [resolvedUrl, artboard, stateMachine, autoplay, loop]);

  if (!resolvedUrl) {
    return (
      <div className="h-36 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
        Add a .riv asset to preview.
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-36 flex items-center justify-center text-xs text-red-500 dark:text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="rounded-xl overflow-hidden border border-blue-900/30 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 aspect-square"
    >
      <canvas ref={canvasRef} className="w-full h-full block" />
      <div className="absolute inset-0 pointer-events-none text-[11px] text-white/70 p-2 flex flex-col gap-1">
        {meta.stateMachines.length > 0 && (
          <div>State machines: {meta.stateMachines.join(', ')}</div>
        )}
        {meta.animations.length > 0 && (
          <div>Animations: {meta.animations.join(', ')}</div>
        )}
        {meta.artboards.length > 0 && (
          <div>Artboards: {meta.artboards.join(', ')}</div>
        )}
        {inputs.length > 0 && <div>Inputs: {inputs.join(', ')}</div>}
        {error && <div className="text-red-200">{error}</div>}
      </div>
    </div>
  );
};

interface LessonBlockCardProps {
  block: LessonBlock;
  pageId: string;
}

export const LessonBlockCard: React.FC<LessonBlockCardProps> = ({ block, pageId }) => {
  const selectBlock = useLessonBuilderStore((s) => s.selectBlock);
  const deleteBlock = useLessonBuilderStore((s) => s.deleteBlock);
  const duplicateBlock = useLessonBuilderStore((s) => s.duplicateBlock);
  const updateBlock = useLessonBuilderStore((s) => s.updateBlock);
  const selectPage = useLessonBuilderStore((s) => s.selectPage);
  const selectedBlockId = useLessonBuilderStore((s) => s.selectedBlockId);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  const preview = () => {
    const payload = block.payload || {};
    switch (block.type) {
      case 'text': {
        const align = payload.align || 'left';
        const showHeading = payload.showHeading !== false;
        const heading = showHeading ? payload.heading : '';
        const body = payload.body || '';
        const useHtml = Boolean(payload.useHtml);
        const rawHtml = (useHtml && payload.html) || (useHtml && block.html) || '';
        const safeHtml = rawHtml ? sanitizeHtml(rawHtml) : '';

        if (useHtml && safeHtml) {
          return (
            <>
              <style>
                {`
                  .rt-scope ul { list-style: disc; padding-left: 1.2rem; margin: 0.35rem 0; }
                  .rt-scope ol { list-style: decimal; padding-left: 1.2rem; margin: 0.35rem 0; }
                  .rt-scope li { margin: 0.15rem 0; }
                  .rt-scope h1 { font-size: 1.35rem; font-weight: 700; margin: 0.4rem 0; }
                  .rt-scope h2 { font-size: 1.2rem; font-weight: 700; margin: 0.35rem 0; }
                  .rt-scope h3 { font-size: 1.05rem; font-weight: 700; margin: 0.3rem 0; }
                  .rt-scope h4 { font-size: 0.95rem; font-weight: 700; margin: 0.25rem 0; }
                  .rt-scope p { margin: 0.3rem 0; }
                  .rt-scope p:empty::before { content: '\\00a0'; display: block; min-height: 1rem; }
                  .rt-scope blockquote { border-left: 3px solid rgba(59,130,246,0.6); padding-left: 0.75rem; margin: 0.4rem 0; color: inherit; }
                  .rt-scope code { background: rgba(148,163,184,0.15); padding: 0.1rem 0.25rem; border-radius: 4px; }
                `}
              </style>
              <div
                className="text-sm text-gray-900 dark:text-gray-50 leading-relaxed prose prose-sm max-w-none dark:prose-invert rt-scope"
                style={{ textAlign: align as any, listStylePosition: 'inside' }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </>
          );
        }

        return (
          <div className="space-y-1" style={{ textAlign: align as any }}>
            {heading ? <div className="text-base font-semibold text-gray-900 dark:text-gray-50">{heading}</div> : null}
            {body ? (
              <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{body}</div>
            ) : (
              !heading && <div className="text-xs text-gray-400 dark:text-gray-500">Add text...</div>
            )}
          </div>
        );
      }
      case 'image': {
        const url = payload.url as string | undefined;
        return (
          <div className="space-y-2">
            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 h-44 relative">
              {url ? (
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: `url(${url})` }}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500 gap-2 text-sm">
                  <ImageIcon className="w-4 h-4" />
                  Add image URL
                </div>
              )}
            </div>
            {(payload.caption || url) && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {payload.caption || url}
              </div>
            )}
          </div>
        );
      }
      case 'rive': {
        const url = payload.asset_url || '';
        const artboard = payload.artboard || 'Default';
        const stateMachine = payload.stateMachine || 'Default';
        return (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <Play className="w-4 h-4" />
              <div className="text-sm font-semibold">Rive Animation</div>
              <div className="ml-auto text-[11px] opacity-80">Artboard: {artboard}</div>
              <div className="text-[11px] opacity-80">State: {stateMachine}</div>
            </div>
            <RivePreview
              assetUrl={url}
              artboard={payload.artboard}
              stateMachine={payload.stateMachine}
              autoplay={payload.autoplay !== false}
              loop={payload.loop}
            />
            <div className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 break-words">
              {url || 'Add .riv asset to see preview'}
            </div>
          </div>
        );
      }
      case 'mcq': {
        const options = payload.options || [];
        const questionAlign = payload.question_align || 'left';
        const questionHtml = payload.question_html || payload.question || '<p>Multiple choice question</p>';
        const safeQuestion = sanitizeHtml(questionHtml);
        return (
          <div className="space-y-2">
            <div
              className="text-sm text-gray-900 dark:text-gray-50 leading-relaxed rt-scope"
              style={{ textAlign: questionAlign as any, listStylePosition: 'inside' }}
              dangerouslySetInnerHTML={{ __html: safeQuestion }}
            />
            <div className="space-y-1.5">
              {options.map((opt: any, idx: number) => (
                <div
                  key={opt.id || idx}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 bg-white dark:bg-gray-900"
                >
                  {opt.is_correct ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="text-sm text-gray-800 dark:text-gray-200">{opt.text || 'Option'}</div>
                </div>
              ))}
              {options.length === 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">Add options to preview.</div>
              )}
            </div>
          </div>
        );
      }
      case 'true_false': {
        return (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {payload.statement || 'True / False statement'}
            </div>
            <div className="flex gap-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  payload.correct === true
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                True
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  payload.correct === false
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                False
              </span>
            </div>
            {payload.explanation && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {payload.explanation}
              </div>
            )}
          </div>
        );
      }
      case 'flip_card_grid': {
        const cards = payload.cards || [];
        const sample = cards.slice(0, 4);
        const columns = Math.min(Math.max(payload.columns || 2, 1), 3);
        return (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">Flip Cards</div>
            <div className={`grid grid-cols-${columns} gap-2`}>
              {sample.map((card: any, idx: number) => (
                <div
                  key={card.id || idx}
                  className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-2 space-y-1"
                >
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Front</div>
                  <div className="text-sm text-gray-900 dark:text-gray-50 line-clamp-2">{card.front || '...'}</div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Back</div>
                  <div className="text-sm text-gray-700 dark:text-gray-200 line-clamp-2">{card.back || '...'}</div>
                </div>
              ))}
              {sample.length === 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 col-span-2">
                  Add cards to preview.
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'link_preview': {
        return (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 space-y-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {payload.title || 'Link title'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
              {payload.description || 'Short description for this link.'}
            </div>
            <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-300 break-all">
              <Link2 className="w-3 h-3" />
              {payload.url || 'https://example.com'}
            </div>
          </div>
        );
      }
      case 'spacer': {
        const h = Math.min(Math.max(Number(payload.height ?? 24), 4), 320);
        return <div style={{ height: `${h}px` }} className="w-full" aria-label="Spacer" />;
      }
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group border rounded-xl p-3 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm transition cursor-pointer ${
        selectedBlockId === block.id ? 'ring-2 ring-blue-500' : ''
      }`}
      onClick={() => {
        selectPage(pageId);
        selectBlock(block.id);
      }}
    >
      <div className="flex items-center gap-2">
        <button
          className="p-1 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 capitalize">
              {block.type.replace('_', ' ')}
            </div>
            <div className="text-[11px] text-gray-500">{block.position + 1}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
          <button
            className="p-1 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200"
            onClick={(e) => {
              e.stopPropagation();
              updateBlock(pageId, block.id, { is_visible: !block.is_visible });
            }}
            title={block.is_visible ? 'Hide block' : 'Show block'}
          >
            {block.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            className="p-1 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200"
            onClick={(e) => {
              e.stopPropagation();
              duplicateBlock(pageId, block.id);
            }}
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            className="p-1 text-red-500 hover:text-red-700"
            onClick={(e) => {
              e.stopPropagation();
              deleteBlock(pageId, block.id);
            }}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="mt-3">{preview()}</div>
    </div>
  );
};
